<template>
  <v-card flat class="valuation-report">
    <v-toolbar dense flat color="grey lighten-3" class="no-print">
      <v-btn icon :aria-label="backLabel" @click="goBack"><v-icon>mdi-arrow-left</v-icon></v-btn>
      <v-toolbar-title>{{ heading }}</v-toolbar-title>
      <v-spacer />
      <v-chip small color="primary" text-color="white">{{ visibleQuantity }} units</v-chip>
    </v-toolbar>

    <v-card-text class="valuation-report__body pa-4 pa-sm-5">
      <div class="text-caption admin-muted mb-3">
        <strong v-if="locationName">{{ locationName }}</strong>
        <span v-if="locationName"> • </span>Current inventory valuation
      </div>
      <v-text-field
        v-model.trim="search"
        outlined
        dense
        clearable
        hide-details
        prepend-inner-icon="mdi-magnify"
        :label="level === 'categories' ? 'Search categories' : 'Search products'"
        class="mb-4"
      />

      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-alert v-else-if="error" type="error" outlined role="alert">
        {{ error }}
        <v-btn text color="primary" min-height="44" @click="loadReport">Try again</v-btn>
      </v-alert>
      <v-alert v-else-if="!currentItems.length" type="info" outlined>
        No inventory with a positive quantity was found.
      </v-alert>
      <v-list v-else two-line class="valuation-report__list pa-0">
        <template v-for="(item, index) in currentItems">
          <v-list-item :key="item.key" :button="level === 'categories'" @click="openItem(item)">
            <v-list-item-avatar color="primary"><v-icon dark>{{ level === "categories" ? "mdi-shape-outline" : "mdi-package-variant-closed" }}</v-icon></v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold" v-html="$options.filters.highlight(item.name, search)" />
              <v-list-item-subtitle>
                {{ item.quantity }} unit{{ item.quantity === 1 ? "" : "s" }} • Cost {{ currency(item.costValue) }} • Retail {{ currency(item.retailValue) }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="valuation-report__action">
              <strong>{{ currency(item.costValue) }}</strong>
              <v-icon v-if="level === 'categories'">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
          <v-divider v-if="index < currentItems.length - 1" :key="`${item.key}-divider`" />
        </template>
      </v-list>
    </v-card-text>

    <v-divider />
    <v-card-actions class="valuation-report__total pa-4">
      <div>
        <div class="text-caption admin-muted">Total inventory value</div>
        <div class="text-body-2">{{ visibleQuantity }} units</div>
      </div>
      <v-spacer />
      <div class="text-right">
        <div><strong>{{ currency(visibleCost) }}</strong> cost</div>
        <div class="text-caption admin-muted">{{ currency(visibleRetail) }} retail</div>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import reportService from "@/modules/ReportView/service";

export default {
  name: "MobileInventoryValuation",
  data: () => ({
    loading: false,
    error: "",
    search: "",
    rows: [],
    locationName: "",
    level: "categories",
    selectedCategory: null,
  }),
  computed: {
    locationId() { return Number(this.$route.query.LocationId); },
    heading() { return this.selectedCategory ? this.selectedCategory.name : "Inventory Valuation"; },
    backLabel() { return this.level === "categories" ? "Back to reports" : "Back to inventory valuation"; },
    categories() {
      const groups = new Map();
      this.rows.forEach((row) => {
        const name = row.category || "Uncategorized";
        if (!groups.has(name)) groups.set(name, { key: `category-${name}`, name, rows: [], quantity: 0, costValue: 0, retailValue: 0 });
        const group = groups.get(name);
        group.rows.push(row);
        group.quantity += Number(row.quantity) || 0;
        group.costValue += Number(row.costValue) || 0;
        group.retailValue += Number(row.retailValue) || 0;
      });
      return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
    products() {
      return this.selectedCategory ? this.selectedCategory.rows.map((row) => ({
        ...row,
        key: `${row.type}-${row.id}`,
        name: row.name || "Unnamed product",
        quantity: Number(row.quantity) || 0,
        costValue: Number(row.costValue) || 0,
        retailValue: Number(row.retailValue) || 0,
      })) : [];
    },
    currentItems() {
      const query = (this.search || "").toLowerCase();
      const items = this.level === "categories" ? this.categories : this.products;
      return items.filter((item) => `${item.name} ${item.sku || ""} ${item.upc || ""}`.toLowerCase().includes(query));
    },
    visibleQuantity() { return this.currentItems.reduce((total, item) => total + (Number(item.quantity) || 0), 0); },
    visibleCost() { return this.currentItems.reduce((total, item) => total + (Number(item.costValue) || 0), 0); },
    visibleRetail() { return this.currentItems.reduce((total, item) => total + (Number(item.retailValue) || 0), 0); },
  },
  created() { this.loadReport(); },
  methods: {
    currency(value) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(value || 0)); },
    async loadReport() {
      if (!Number.isSafeInteger(this.locationId) || this.locationId <= 0) {
        this.error = "A valid location is required to open this report.";
        return;
      }
      this.loading = true;
      this.error = "";
      try {
        const response = await reportService.Location.inventoryValuation({ LocationId: this.locationId });
        this.rows = (response.data && response.data.rows) || [];
        this.locationName = (response.data && response.data.locationName) || "";
      } catch (error) {
        this.error = "Inventory valuation could not be loaded. Check your connection and try again.";
      } finally { this.loading = false; }
    },
    openItem(item) {
      if (this.level !== "categories") return;
      this.selectedCategory = item;
      this.level = "products";
      this.search = "";
    },
    goBack() {
      if (this.level === "products") {
        this.level = "categories";
        this.selectedCategory = null;
        this.search = "";
        return;
      }
      this.$router.back();
    },
  },
};
</script>

<style scoped>
.valuation-report { display: flex; flex-direction: column; height: calc(100dvh - 48px); margin: 0 auto; max-width: 900px; overflow: hidden; }
.valuation-report > .v-toolbar { flex: none; }
.valuation-report__body { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior-y: contain; touch-action: pan-y; -webkit-overflow-scrolling: touch; }
.valuation-report__list { border: 1px solid rgba(0, 0, 0, .14); border-radius: 14px; overflow: hidden; }
.theme--dark .valuation-report__list { border-color: rgba(255, 255, 255, .2); }
.valuation-report__action { align-items: flex-end; gap: 4px; min-width: 92px; }
.valuation-report__total { flex: none; }
</style>
